import { useCallback, useMemo, useState } from "react";
import { Tooltip } from "antd";
import { Download } from "react-feather";

import { Button } from "../index";
import { AppStrings } from "../../../utils/constants/AppStrings";
import type { AsyncExportState } from "../../../queries/useAsyncExport.types";
import AsyncExportDrawer from "./AsyncExportDrawer";
import styles from "./AsyncExportButton.module.css";
import type { AsyncExportLabels, ExportColumnOption } from "./types";

// Loose payload type: the button never inspects the request body — it just
// forwards whatever `buildPayload()` returns to the state machine (which is
// generic over payload shape too).
type ExportPayload = Record<string, unknown>;

export type AsyncExportButtonProps = {
  /** Full vocabulary of columns the admin may pick from. Order = render order. */
  readonly columns: readonly ExportColumnOption[];
  /**
   * Called at submit time to snapshot the current list filters into the
   * export POST body. Function-shaped so we always read fresh values from
   * the parent (a memoized object could go stale between opens).
   */
  readonly buildPayload: () => ExportPayload;
  /**
   * Domain-owned export state machine (typically a thin wrapper over
   * `useAsyncExport`, e.g. `useUsersExport`). The button drives it via
   * `start()` and mirrors `isBusy` on the outer button.
   */
  readonly exportState: Pick<AsyncExportState<ExportPayload>, "isBusy" | "start">;
  /** Disables the outer button (e.g. an unexportable filter is active). */
  readonly disabled?: boolean;
  /** Tooltip copy shown when `disabled` is true. */
  readonly disabledTooltip?: string;
  /** Optional overrides for aria-label + drawer chrome copy. */
  readonly labels?: AsyncExportLabels;
  /** Starts immediately when the endpoint always exports its fixed CSV schema. */
  readonly directExport?: boolean;
};

/**
 * Domain-agnostic export button + column picker drawer.
 *
 * Owns the drawer open/close state and the column selection set. The
 * async job state machine (`isBusy`, `start`) is injected via `exportState`
 * so the button can be reused across features that hit different
 * endpoints or need different error copy.
 *
 * Submit contract:
 *   - `selected === all columns` → omit `columns` from the payload (BE
 *     returns the caller's full column set).
 *   - `0 < selected < all` → send the exact `columns` array.
 *   - `selected.size === 0`  → confirm button disabled at the drawer
 *     level; never reaches this code.
 */
function AsyncExportButton({
  columns,
  buildPayload,
  exportState,
  disabled = false,
  disabledTooltip,
  labels,
  directExport = false,
}: AsyncExportButtonProps) {
  const allKeys = useMemo(
    () => new Set(columns.map((c) => c.key)),
    [columns],
  );
  // Locked keys are always in the selection set and cannot be deselected.
  // The drawer disables the corresponding checkbox; the button treats them
  // as the floor of "Select All OFF" and as guaranteed payload columns.
  const lockedKeys = useMemo(
    () => new Set(columns.filter((c) => c.locked).map((c) => c.key)),
    [columns],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(allKeys),
  );

  const { isBusy, start } = exportState;

  const openDrawer = useCallback(() => {
    // Reset to "all selected" each time the drawer opens so admins aren't
    // stuck with a stale selection from a previous session.
    setSelectedKeys(new Set(allKeys));
    setDrawerOpen(true);
  }, [allKeys]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const toggleColumn = useCallback(
    (key: string, checked: boolean) => {
      // Defensive: the drawer already prevents unchecking a locked key,
      // but a stray call from another surface (custom drawer composition,
      // future keyboard shortcut, tests) shouldn't be able to remove one.
      if (lockedKeys.has(key)) return;
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        if (checked) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    [lockedKeys],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      // `Select All OFF` clears everything except locked keys — they can't
      // be excluded from the payload by contract.
      setSelectedKeys(checked ? new Set(allKeys) : new Set(lockedKeys));
    },
    [allKeys, lockedKeys],
  );

  const handleSubmit = useCallback(() => {
    if (selectedKeys.size === 0) return;

    const basePayload = buildPayload();

    // When the admin left every box checked we omit `columns` so the BE
    // decides the full set (matches per-brand column sets transparently).
    const payload: ExportPayload =
      selectedKeys.size === allKeys.size
        ? basePayload
        : {
            ...basePayload,
            columns: columns
              .filter((c) => selectedKeys.has(c.key) || c.locked)
              .map((c) => c.key),
          };

    start(payload);
    setDrawerOpen(false);
  }, [selectedKeys, buildPayload, allKeys.size, columns, start]);

  const ariaLabel =
    labels?.buttonAriaLabel ?? AppStrings.EXPORT_BUTTON_DEFAULT_ARIA;

  const outerButton = (
    <Button
      intent="primary"
      size="large"
      onClick={() => {
        if (directExport) {
          start(buildPayload());
          return;
        }
        openDrawer();
      }}
      disabled={disabled || isBusy}
      loading={isBusy}
      aria-label={ariaLabel}
      data-testid="async-export-button"
      className={styles.exportButton}
    >
      {!isBusy && (
        <Download size={16} className={styles.exportIcon} />
      )}
    </Button>
  );

  return (
    <>
      {disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} placement="topRight">
          <div
            className={styles.buttonWrapper}
            data-testid="async-export-button-wrapper"
          >
            {outerButton}
          </div>
        </Tooltip>
      ) : (
        <div
          className={styles.buttonWrapper}
          data-testid="async-export-button-wrapper"
        >
          {outerButton}
        </div>
      )}

      {!directExport ? (
        <AsyncExportDrawer
          open={drawerOpen}
          onClose={closeDrawer}
          columns={columns}
          selectedKeys={selectedKeys}
          onToggleColumn={toggleColumn}
          onToggleAll={toggleAll}
          onSubmit={handleSubmit}
          submitting={isBusy}
          labels={labels}
        />
      ) : null}
    </>
  );
}

export default AsyncExportButton;
